import { Alert, CircularProgress, Grid, Typography } from '@mui/material';
import { useDebounce } from 'usehooks-ts';
import Button from 'components/Button';
import MultiPageBuilder from 'components/MultiPageBuilder';
import FullHeightView from 'components/new/FullHeightView';
import PageLoader from 'components/PageLoader';
import Spacing from 'components/Spacing';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { Row } from 'further-ui/components/Layout';
import { numberToCurrencyString } from 'further-ui/utils';
import { round, sumBy } from 'lodash';
import { FC, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  Action,
  UpdateCapitalActionFormValues,
} from '../hooks/useCapitalActionForm';
import useDefaultEntryValues from '../hooks/useDefaultEntryValues';
import useGetEntryTitle from '../hooks/useGetEntryTitle';
import CapitalActionEntry from './CapitalActionEntry';
import useTotalCapitalContributionUsed from './useTotalCapitalContributionUsed';

type Props = {
  lastAction: Action | null;
  onPrevious: () => void;
  onSaveAndExit: () => void;
  onNext: () => void;
  hasUncalledEqualisations: boolean;
  isLoading: boolean;
};

const CapitalActionEntries: FC<Props> = ({
  lastAction,
  onPrevious,
  onSaveAndExit,
  onNext,
  hasUncalledEqualisations,
  isLoading,
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const getEntryTitle = useGetEntryTitle();
  const { defaultEntry } = useDefaultEntryValues();

  const { vehicle } = useLayoutContext();
  const {
    formMethods: { control, watch, formState },
  } = useFormWithStateContext<UpdateCapitalActionFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const capitalActionEntries = watch('entries') ?? [];
  const debouncedEntries = useDebounce(capitalActionEntries, 300);

  const handleAddAnotherTransaction = () => {
    append(defaultEntry);
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    } else {
      onPrevious();
    }
  };

  const { total: totalCapitalContributionUsed, isLoading: isTotalLoading } =
    useTotalCapitalContributionUsed({
      vehicleId: vehicle?._id ?? '',
      commitmentType: vehicle?.commitmentType,
      entries: debouncedEntries,
    });

  const totalAmount = round(
    sumBy(capitalActionEntries, (entry) =>
      [
        CapitalActionEntryType.CapitalRefund,
        CapitalActionEntryType.CapitalDistribution,
      ].includes(entry.entryType)
        ? -entry.totalAmountLcy
        : entry.totalAmountLcy,
    ) - totalCapitalContributionUsed,
    2,
  );

  if (!vehicle) {
    return <PageLoader />;
  }

  const hasDistributionEntry = capitalActionEntries.some(
    (entry) => entry.entryType === CapitalActionEntryType.CapitalDistribution,
  );

  return (
    <FullHeightView
      actionBar={
        <Row centered spacing="sm" justify="space-between">
          <Button
            size="large"
            variant="outlined"
            onClick={handlePrevious}
            disabled={isLoading}
          >
            Previous step
          </Button>
          <Row centered spacing="sm" justify="flex-end">
            <Button
              size="large"
              variant="outlined"
              onClick={onSaveAndExit}
              loading={lastAction === 'saveAndExit' && isLoading}
              disabled={isLoading}
            >
              Save and exit
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={handleAddAnotherTransaction}
              disabled={isLoading || hasDistributionEntry}
            >
              Add another transaction
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={onNext}
              loading={lastAction === 'saveAndNext' && isLoading}
              disabled={isLoading}
            >
              Save and review
            </Button>
          </Row>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 11 }}>
          <Spacing size="md">
            <Typography variant="h4">
              Add capital call or distribution transaction
            </Typography>
            <MultiPageBuilder
              summaryHeader="Capital action summary"
              summaryFooterTitle="Total to be called/(distributed)"
              summaryFooterTotals={
                isTotalLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography fontWeight="bold">
                    {numberToCurrencyString(totalAmount, {
                      currency: vehicle.baseCurrency,
                      negativeInBrackets: true,
                    })}
                  </Typography>
                )
              }
              currentPageIndex={currentPageIndex}
              onPageChange={setCurrentPageIndex}
              defaultActivePageIndex={
                capitalActionEntries.length
                  ? capitalActionEntries.length - 1
                  : 0
              }
              onDeletePage={remove}
              pageName="transaction"
            >
              {capitalActionEntries.map((entry, index) => (
                <MultiPageBuilder.Page
                  key={fields[index].id}
                  title={getEntryTitle(entry)}
                  totals={numberToCurrencyString(entry.totalAmountLcy, {
                    currency: vehicle.baseCurrency,
                  })}
                >
                  <CapitalActionEntry
                    hasUncalledEqualisations={hasUncalledEqualisations}
                    index={index}
                    vehicleId={vehicle._id}
                    baseCurrency={vehicle.baseCurrency}
                    hasDistributionEntry={hasDistributionEntry}
                    entriesCount={capitalActionEntries.length}
                  />
                </MultiPageBuilder.Page>
              ))}
            </MultiPageBuilder>
            {!!formState.errors.entries?.message && (
              <Alert severity="error">{formState.errors.entries.message}</Alert>
            )}
          </Spacing>
        </Grid>
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionEntries;
